import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { RequireAuth } from './auth/RequireAuth';
import { AuthCallback } from './pages/AuthCallback';
import { Landing } from './pages/Landing';
import { Onboarding } from './pages/Onboarding';
import { Home } from './pages/Home';
import { Members } from './pages/Members';
import { Schedule } from './pages/Schedule';
import { ActivityDetail } from './pages/ActivityDetail';
import { Signups } from './pages/Signups';
import { Attendance } from './pages/Attendance';
import { Chat } from './pages/Chat';
import { WebsitePage } from './pages/Website';
import { WebsiteEditorPage } from './pages/WebsiteEditor';
import { WebsiteDesignPage } from './pages/WebsiteDesign';
import { BroadcastsPage } from './pages/Broadcasts';
import { BroadcastEditorPage } from './pages/BroadcastEditor';
import { PublicInvoice } from './pages/PublicInvoice';
import { PublicMemberCard } from './pages/PublicMemberCard';
import { PublicStorefront } from './pages/PublicStorefront';
import { PublicNotice } from './pages/PublicNotice';
import PublicCardSetup from './pages/PublicCardSetup';
import { MemberCard } from './pages/MemberCard';
import { Billing } from './pages/Billing';
import { Notices } from './pages/Notices';
import { Pulse } from './pages/Pulse';
import { Assistant } from './pages/Assistant';
import { AssistantDetail } from './pages/AssistantDetail';
import { Shop } from './pages/Shop';
import { ShopTeam } from './pages/ShopTeam';
import { ShopStore } from './pages/ShopStore';
import { Activity } from './pages/settings/Activity';
import { App as AppSettings } from './pages/settings/App';
import { Administrators } from './pages/settings/Administrators';
import { TeamCategories } from './pages/settings/TeamCategories';
import { Billing as BillingSettings } from './pages/settings/Billing';
import { DangerZone } from './pages/settings/DangerZone';
import { Feedback } from './pages/settings/Feedback';
import { FeedbackDetail } from './pages/settings/FeedbackDetail';
import { General } from './pages/settings/General';
import { MemberCardTemplates } from './pages/settings/MemberCardTemplates';
import { Notifications } from './pages/settings/Notifications';
import { Profile } from './pages/settings/Profile';
import { DashboardShell } from './components/shell/DashboardShell';

// App is the route tree. /auth/callback is intentionally outside RequireAuth
// because the callback page IS the path that completes the login — gating it
// would loop forever. /orgs/:slug/* renders inside DashboardShell so every
// org-scoped page shares the sidebar + nav chrome.
export function App() {
  return (
    <Routes>
      <Route path="/auth/callback" element={<AuthCallback />} />
      {/* Public invoice payment page — D-8 token mode. NO auth wrapper:
          the token in the URL is the credential. */}
      <Route path="/pay/:token" element={<PublicInvoice />} />
      <Route path="/store/:token" element={<PublicStorefront />} />
      <Route path="/store/sell/:token" element={<PublicStorefront seller />} />
      {/* Public member-card scan page — token in URL is the credential. */}
      <Route path="/m/c/:token" element={<PublicMemberCard />} />
      {/* Public notice page — token in URL is the credential (Aviseringar). */}
      <Route path="/n/:token" element={<PublicNotice />} />
      {/* Card-entry step of the member app's recurring-payment flow. NO auth
          wrapper: the app authenticated the caller and enforced scope + mandate
          when it minted the SetupIntent, and the client_secret it passes in the
          URL fragment permits only attaching a card to that one SetupIntent. */}
      <Route path="/card-setup" element={<PublicCardSetup />} />
      <Route
        path="/"
        element={
          <RequireAuth>
            <Landing />
          </RequireAuth>
        }
      />
      <Route
        path="/onboarding"
        element={
          <RequireAuth>
            <Onboarding />
          </RequireAuth>
        }
      />
      {/* Legacy URL — keep working for any in-flight links / browser bookmarks. */}
      <Route path="/orgs/new" element={<Navigate to="/onboarding" replace />} />
      <Route
        path="/orgs/:slug"
        element={
          <RequireAuth>
            <DashboardShell />
          </RequireAuth>
        }
      >
        <Route index element={<Home />} />
        <Route path="members" element={<Members />} />
        {/* Teams + signups merged into one page (Signups component). The old
            standalone Teams page was retired; /signups redirects for any
            existing links/bookmarks. */}
        <Route path="teams" element={<Signups />} />
        <Route path="signups" element={<Navigate to="../teams" replace />} />
        <Route path="schedule" element={<Schedule />} />
        <Route path="schedule/activities/:id" element={<ActivityDetail />} />
        <Route path="attendance" element={<Attendance />} />
        <Route path="chat" element={<Chat />} />
        <Route path="website" element={<WebsitePage />} />
        <Route path="website/:websiteId" element={<WebsiteEditorPage />} />
        <Route path="website/:websiteId/design" element={<WebsiteDesignPage />} />
        <Route path="broadcasts" element={<BroadcastsPage />} />
        <Route path="broadcasts/:id" element={<BroadcastEditorPage />} />
        {/* Locations and templates were folded into the schedule workspace.
            Old links keep working via redirects that translate to query params. */}
        <Route path="locations" element={<ScheduleRedirect search="?panel=locations" />} />
        <Route path="templates" element={<ScheduleRedirect search="?view=templates" />} />
        <Route path="templates/:templateId" element={<TemplateRedirect />} />
<Route path="settings/activity" element={<Activity />} />
        <Route path="settings/administrators" element={<Administrators />} />
        <Route path="settings/team-categories" element={<TeamCategories />} />
        <Route path="settings/organization" element={<General />} />
        <Route path="billing" element={<Billing />} />
        <Route path="notices" element={<Notices />} />
        <Route path="pulse" element={<Pulse />} />
        <Route path="shop" element={<Shop />} />
        <Route path="shop/teams/:teamID" element={<ShopTeam />} />
        <Route path="shop/stores/:storeID" element={<ShopStore />} />
        <Route path="settings/billing" element={<BillingSettings />} />
        <Route path="settings/membercard-templates" element={<MemberCardTemplates />} />
        <Route path="settings/profile" element={<Profile />} />
        <Route path="settings/notifications" element={<Notifications />} />
        <Route path="settings/app" element={<AppSettings />} />
        <Route path="members/:memberID/card" element={<MemberCard />} />
        <Route path="settings/feedback" element={<Feedback />} />
        <Route path="settings/feedback/:ticketID" element={<FeedbackDetail />} />
        <Route path="settings/danger-zone" element={<DangerZone />} />
        <Route path="assistant" element={<Assistant />} />
        <Route path="assistant/:suggestionID" element={<AssistantDetail />} />
      </Route>
    </Routes>
  );
}

function ScheduleRedirect({ search }: { search: string }) {
  const { slug = '' } = useParams<{ slug: string }>();
  return <Navigate to={`/orgs/${slug}/schedule${search}`} replace />;
}

function TemplateRedirect() {
  const { slug = '', templateId = '' } = useParams<{ slug: string; templateId: string }>();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  params.set('view', 'templates');
  if (templateId) params.set('template', templateId);
  return <Navigate to={`/orgs/${slug}/schedule?${params.toString()}`} replace />;
}
